import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send, Loader2, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ["Ohio", "Other (Midwest)", "Other"];

const OFFICES = [
    "Central Committee",
    "School Board",
    "City Council",
    "Township Trustee",
    "Mayor",
    "County Commissioner",
    "State Legislature",
    "Statewide",
    "Other / Exploring",
];

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        office_sought: "",
        state: "",
        message: "",
        sms_consent: false,
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email, and message.");
            return;
        }
        if (form.sms_consent && !form.phone.trim()) {
            toast.error("Please provide a phone number to receive text messages.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                ...form,
                phone: form.phone || null,
                office_sought: form.office_sought || null,
                state: form.state || null,
                sms_consent: !!form.sms_consent,
            });
            toast.success(
                "Message received. Nick will be in touch within 1–2 business days.",
            );
            setForm({
                name: "",
                email: "",
                phone: "",
                office_sought: "",
                state: "",
                message: "",
                sms_consent: false,
            });
        } catch (err) {
            const detail =
                err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-28 md:py-36 bg-ink overflow-hidden"
        >
            <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_80%_10%,rgba(201,162,39,0.08),transparent_60%)]" />

            <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="h-px w-8 bg-gold" />
                            <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-semibold">
                                07 / Get In Touch
                            </span>
                        </div>
                        <h2
                            data-testid="contact-headline"
                            className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight"
                        >
                            Running for office?
                            <br />
                            <span className="italic text-gold">Let's talk.</span>
                        </h2>
                        <p className="mt-8 text-cream/65 text-base md:text-lg leading-relaxed max-w-md">
                            Whether you're months out from a local race or already
                            into a competitive statewide effort, the first call is
                            free. Tell us about your campaign and we'll get back to
                            you fast.
                        </p>

                        <div className="mt-12 space-y-6">
                            <a
                                href="mailto:nrkraus@nicholaskrausconsulting.com"
                                data-testid="contact-email-link"
                                className="group flex items-start gap-4 text-cream/80 hover:text-gold transition-colors"
                            >
                                <span className="mt-1 w-10 h-10 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-ink transition-colors">
                                    <Mail size={16} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">
                                        Email
                                    </div>
                                    <div className="font-display text-xl md:text-2xl">
                                        nrkraus@nicholaskrausconsulting.com
                                    </div>
                                </div>
                            </a>
                            <a
                                href="tel:+19373213576"
                                data-testid="contact-phone-link"
                                className="group flex items-start gap-4 text-cream/80 hover:text-gold transition-colors"
                            >
                                <span className="mt-1 w-10 h-10 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-ink transition-colors">
                                    <Phone size={16} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">
                                        Phone
                                    </div>
                                    <div className="font-display text-xl md:text-2xl">
                                        937 · 321 · 3576
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>

                    {/* Right form */}
                    <motion.form
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="lg:col-span-7 bg-[#080808] border border-gold/20 p-8 md:p-12"
                    >
                        <div className="grid sm:grid-cols-2 gap-6">
                            <div>
                                <Label
                                    htmlFor="name"
                                    className="text-[10px] uppercase tracking-[0.3em] text-cream/60"
                                >
                                    Name *
                                </Label>
                                <Input
                                    id="name"
                                    data-testid="contact-input-name"
                                    value={form.name}
                                    onChange={(e) => update("name", e.target.value)}
                                    required
                                    placeholder="Your full name"
                                    className="mt-2 bg-ink border-ink-border focus-visible:ring-gold focus-visible:border-gold rounded-none h-12 text-cream placeholder:text-cream/30"
                                />
                            </div>
                            <div>
                                <Label
                                    htmlFor="email"
                                    className="text-[10px] uppercase tracking-[0.3em] text-cream/60"
                                >
                                    Email *
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    data-testid="contact-input-email"
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    required
                                    placeholder="you@email.com"
                                    className="mt-2 bg-ink border-ink-border focus-visible:ring-gold focus-visible:border-gold rounded-none h-12 text-cream placeholder:text-cream/30"
                                />
                            </div>
                            <div>
                                <Label
                                    htmlFor="phone"
                                    className="text-[10px] uppercase tracking-[0.3em] text-cream/60"
                                >
                                    Phone
                                </Label>
                                <Input
                                    id="phone"
                                    data-testid="contact-input-phone"
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    placeholder="(optional)"
                                    className="mt-2 bg-ink border-ink-border focus-visible:ring-gold focus-visible:border-gold rounded-none h-12 text-cream placeholder:text-cream/30"
                                />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-[0.3em] text-cream/60">
                                    State
                                </Label>
                                <Select
                                    value={form.state}
                                    onValueChange={(v) => update("state", v)}
                                >
                                    <SelectTrigger
                                        data-testid="contact-select-state"
                                        className="mt-2 bg-ink border-ink-border focus:ring-gold rounded-none h-12 text-cream"
                                    >
                                        <SelectValue placeholder="Select state" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-ink border-ink-border text-cream">
                                        {STATES.map((s) => (
                                            <SelectItem
                                                key={s}
                                                value={s}
                                                data-testid={`state-option-${s}`}
                                                className="focus:bg-gold/15 focus:text-gold"
                                            >
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-[10px] uppercase tracking-[0.3em] text-cream/60">
                                    Office Sought
                                </Label>
                                <Select
                                    value={form.office_sought}
                                    onValueChange={(v) => update("office_sought", v)}
                                >
                                    <SelectTrigger
                                        data-testid="contact-select-office"
                                        className="mt-2 bg-ink border-ink-border focus:ring-gold rounded-none h-12 text-cream"
                                    >
                                        <SelectValue placeholder="Select office (or describe in message)" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-ink border-ink-border text-cream max-h-72">
                                        {OFFICES.map((o) => (
                                            <SelectItem
                                                key={o}
                                                value={o}
                                                data-testid={`office-option-${o.replace(/\s+/g, "-").toLowerCase()}`}
                                                className="focus:bg-gold/15 focus:text-gold"
                                            >
                                                {o}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label
                                    htmlFor="message"
                                    className="text-[10px] uppercase tracking-[0.3em] text-cream/60"
                                >
                                    Message *
                                </Label>
                                <Textarea
                                    id="message"
                                    data-testid="contact-input-message"
                                    value={form.message}
                                    onChange={(e) =>
                                        update("message", e.target.value)
                                    }
                                    required
                                    rows={5}
                                    placeholder="Tell us about your race — district, timeline, what you need help with."
                                    className="mt-2 bg-ink border-ink-border focus-visible:ring-gold focus-visible:border-gold rounded-none text-cream placeholder:text-cream/30"
                                />
                            </div>

                            {/* SMS opt-in — required for i360 texting compliance */}
                            <div
                                data-testid="sms-optin-block"
                                className="sm:col-span-2 border border-gold/25 bg-[#050505] p-5 md:p-6 mt-2"
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <MessageSquare
                                        size={14}
                                        className="text-gold"
                                    />
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">
                                        Optional · Text Message Updates
                                    </div>
                                </div>
                                <label
                                    className="flex items-start gap-3 cursor-pointer group"
                                    htmlFor="sms_consent"
                                >
                                    <Checkbox
                                        id="sms_consent"
                                        data-testid="contact-sms-consent"
                                        checked={form.sms_consent}
                                        onCheckedChange={(v) =>
                                            update("sms_consent", v === true)
                                        }
                                        className="mt-0.5 border-gold/60 data-[state=checked]:bg-gold data-[state=checked]:text-ink data-[state=checked]:border-gold"
                                    />
                                    <span className="text-cream/80 text-sm leading-relaxed">
                                        I consent to receive text messages
                                        from Nicholas Kraus Consulting at the
                                        phone number I provided. I have read
                                        and agree to the{" "}
                                        <Link
                                            to="/privacy"
                                            target="_blank"
                                            data-testid="sms-privacy-link"
                                            className="text-gold hover:text-gold-light underline"
                                        >
                                            Privacy Policy
                                        </Link>{" "}
                                        and{" "}
                                        <Link
                                            to="/terms"
                                            target="_blank"
                                            data-testid="sms-terms-link"
                                            className="text-gold hover:text-gold-light underline"
                                        >
                                            Terms &amp; Conditions
                                        </Link>
                                        .
                                    </span>
                                </label>
                                <div className="mt-4 text-xs text-cream/55 leading-relaxed space-y-1">
                                    <p>
                                        <span className="text-cream/80">
                                            What to expect:
                                        </span>{" "}
                                        Nicholas Kraus Consulting will send
                                        campaign updates, event information,
                                        and voting-related reminders (dates
                                        &amp; deadlines) for upcoming
                                        elections.
                                    </p>
                                    <p>
                                        <span className="text-cream/80">
                                            Frequency:
                                        </span>{" "}
                                        Opted-in users may receive
                                        approximately 2–5 text messages per
                                        month. Message and data rates may
                                        apply.
                                    </p>
                                    <p>
                                        <span className="text-cream/80">
                                            Opt out:
                                        </span>{" "}
                                        Reply <strong className="text-gold">STOP</strong>{" "}
                                        at any time to end texts. Reply{" "}
                                        <strong className="text-gold">HELP</strong>{" "}
                                        for more information, or visit{" "}
                                        <a
                                            href="https://nicholaskrausconsulting.com"
                                            className="text-gold hover:text-gold-light underline"
                                        >
                                            nicholaskrausconsulting.com
                                        </a>{" "}
                                        for help.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <p className="text-[10px] uppercase tracking-[0.3em] text-cream/40">
                                We respond within 1–2 business days
                            </p>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit-button"
                                className="inline-flex items-center justify-center gap-3 bg-gold text-ink px-8 py-4 uppercase tracking-[0.18em] text-xs font-bold hover:bg-gold-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2
                                            size={16}
                                            className="animate-spin"
                                        />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        <Send size={14} />
                                        Send Message
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}
